/**
 * Chord Quality References
 *
 * Emotional character and sound descriptions to help users recognize chord qualities.
 * Unlike intervals (which map to song references), chords are better recognized
 * by their emotional quality and harmonic character.
 */

export interface ChordReference {
    name: string;
    displayName: string;
    character: string;       // Emotional quality
    soundDescription: string; // What it sounds like
    intervalStructure: string; // Brief interval breakdown
}

export const chordReferences: Record<string, ChordReference> = {
    "MAJOR": {
        name: "MAJOR",
        displayName: "Major",
        character: "Happy, bright, stable",
        soundDescription: "Full and complete, like sunshine",
        intervalStructure: "Root + Major 3rd + Perfect 5th"
    },
    "MINOR": {
        name: "MINOR",
        displayName: "Minor",
        character: "Sad, dark, introspective",
        soundDescription: "Melancholic but stable",
        intervalStructure: "Root + Minor 3rd + Perfect 5th"
    },
    "DIMINISHED": {
        name: "DIMINISHED",
        displayName: "Diminished",
        character: "Tense, unstable, eerie",
        soundDescription: "Wants to resolve, spooky",
        intervalStructure: "Root + Minor 3rd + Tritone"
    },
    "AUGMENTED": {
        name: "AUGMENTED",
        displayName: "Augmented",
        character: "Dreamy, mysterious, floating",
        soundDescription: "Unresolved, dreamlike quality",
        intervalStructure: "Root + Major 3rd + Augmented 5th"
    },
    "MAJOR_7TH": {
        name: "MAJOR_7TH",
        displayName: "Major 7th",
        character: "Lush, jazzy, sophisticated",
        soundDescription: "Rich and colorful, slightly dreamy",
        intervalStructure: "Major + Major 7th"
    },
    "MINOR_7TH": {
        name: "MINOR_7TH",
        displayName: "Minor 7th",
        character: "Smooth, mellow, soulful",
        soundDescription: "Warm and relaxed",
        intervalStructure: "Minor + Minor 7th"
    },
    "DOMINANT_7TH": {
        name: "DOMINANT_7TH",
        displayName: "Dominant 7th",
        character: "Bluesy, tense, wants resolution",
        soundDescription: "Strong pull toward resolution",
        intervalStructure: "Major + Minor 7th"
    },
    "SUSPENDED_2ND": {
        name: "SUSPENDED_2ND",
        displayName: "Suspended 2nd",
        character: "Open, ambiguous, modern",
        soundDescription: "Neither happy nor sad",
        intervalStructure: "Root + Major 2nd + Perfect 5th"
    },
    "SUSPENDED_4TH": {
        name: "SUSPENDED_4TH",
        displayName: "Suspended 4th",
        character: "Anticipatory, unresolved",
        soundDescription: "Wants to move to major or minor",
        intervalStructure: "Root + Perfect 4th + Perfect 5th"
    },
    "POWER": {
        name: "POWER",
        displayName: "Power Chord",
        character: "Strong, neutral, rock",
        soundDescription: "Neither major nor minor, raw power",
        intervalStructure: "Root + Perfect 5th"
    }
};

// Helper to get reference by chord type name
export const getChordReference = (name: string): ChordReference | undefined => {
    return chordReferences[name];
};

// Helper to get all chord references as array
export const getAllChordReferences = (): ChordReference[] => {
    return Object.values(chordReferences);
};
